import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['button', 'indicator', 'label']

  static values = {
    url: String,
    online: Boolean
  }

  connect() {
    // Fetch current status from server to handle Turbo cache staleness
    this.refreshStatus()
  }

  async refreshStatus() {
    try {
      const response = await fetch(this.urlValue, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.onlineValue = data.is_online ?? false
        this.updateUI()
        // Update data attribute for future cache
        this.element.dataset.adminSupportStatusToggleOnlineValue = this.onlineValue
      }
    } catch (error) {
      // Fall back to server-rendered value on error
      console.warn('Failed to refresh status, using cached value:', error)
      this.updateUI()
    }
  }

  async toggle() {
    const newStatus = !this.onlineValue

    this.setLoading(true)

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ is_online: newStatus })
      })

      const data = await response.json()

      if (data.success) {
        // Use the server's confirmed status (in case of race conditions)
        const confirmedStatus = data.status?.is_online ?? newStatus
        this.onlineValue = confirmedStatus
        this.updateUI()
        // Update the data attribute for Turbo cache
        this.element.dataset.adminSupportStatusToggleOnlineValue = confirmedStatus
      } else {
        this.showNotification('Failed to update status', 'error')
      }
    } catch (error) {
      console.error('Status toggle error:', error)
      this.showNotification('Failed to update status', 'error')
    } finally {
      this.setLoading(false)
    }
  }

  updateUI() {
    if (this.onlineValue) {
      // Going online
      this.buttonTarget.classList.remove('bg-neutral-100', 'text-neutral-600', 'hover:bg-neutral-200')
      this.buttonTarget.classList.add('bg-emerald-500', 'text-white', 'hover:bg-emerald-600')
      this.indicatorTarget.classList.remove('bg-neutral-400')
      this.indicatorTarget.classList.add('bg-white', 'animate-pulse')
      this.labelTarget.textContent = 'LIVE'
    } else {
      // Going offline
      this.buttonTarget.classList.remove('bg-emerald-500', 'text-white', 'hover:bg-emerald-600')
      this.buttonTarget.classList.add('bg-neutral-100', 'text-neutral-600', 'hover:bg-neutral-200')
      this.indicatorTarget.classList.remove('bg-white', 'animate-pulse')
      this.indicatorTarget.classList.add('bg-neutral-400')
      this.labelTarget.textContent = 'OFFLINE'
    }
  }

  setLoading(isLoading) {
    this.buttonTarget.disabled = isLoading
    if (isLoading) {
      this.buttonTarget.classList.add('opacity-50', 'cursor-wait')
    } else {
      this.buttonTarget.classList.remove('opacity-50', 'cursor-wait')
    }
  }

  showNotification(message, type = 'info') {
    const colors = {
      info: 'bg-blue-50 border-blue-500 text-blue-700',
      success: 'bg-green-50 border-green-500 text-green-700',
      error: 'bg-red-50 border-red-500 text-red-700'
    }

    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 rounded border-2 ${colors[type] || colors.info}`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 5000)
  }
}
